import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle, Phone, Mail } from "lucide-react";
import SEO from "../components/SEO";
import CalendlyEmbed from "../components/CalendlyEmbed";

export const ThankYou = () => {
  const location = useLocation();
  const estimatedSaving = location.state?.estimatedSaving || null;

  useEffect(() => {
    // Track thank you page view
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/analytics/contact-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: 'thank_you_page',
        estimated_saving: estimatedSaving || 0
      })
    }).catch(err => console.log('Analytics tracking failed'));
  }, [estimatedSaving]);

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <SEO
        title="Thank You | Brightson Accounting"
        description="Thank you for contacting Brightson Accounting. We'll be in touch shortly."
        canonical="/thank-you"
      />

      <div style={{ maxWidth: '600px', textAlign: 'center' }}>
        {/* Success Icon */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
          <CheckCircle size={80} color="#10b981" />
        </div>

        {/* Main Message */}
        <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: '#1e3a8a' }}>
          Thanks — we've received your details
        </h1>

        <p style={{ fontSize: '1.1rem', color: '#4b5563', marginBottom: '2rem', lineHeight: '1.6' }}>
          We'll review your information and get back to you within 24 hours.
        </p>

        {/* Estimated Saving Display */}
        {estimatedSaving && estimatedSaving > 0 && (
          <div style={{ 
            background: '#f0fdf4', 
            border: '2px solid #10b981', 
            borderRadius: '8px', 
            padding: '1.5rem', 
            marginBottom: '2rem' 
          }}>
            <p style={{ margin: 0, fontSize: '1rem', color: '#065f46', fontWeight: 500 }}>
              Based on your estimate, you could be saving <strong>£{estimatedSaving.toLocaleString()}</strong> annually.
            </p>
          </div>
        )}

        {/* Urgent Query Section */}
        <div style={{ 
          background: '#f3f4f6', 
          borderRadius: '8px', 
          padding: '2rem', 
          marginBottom: '2rem' 
        }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '0.75rem', color: '#1e3a8a' }}>
            Want to speak sooner?
          </h2>
          <p style={{ fontSize: '1rem', color: '#4b5563', marginBottom: '1.5rem' }}>
            If your query is urgent, you can book a call instantly.
          </p>

          <Link
            to="#calendar"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: '#10b981',
              color: 'white',
              padding: '1rem 2rem',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              transition: 'background 0.3s',
              marginBottom: '2rem'
            }}
            onMouseOver={(e) => e.target.style.background = '#059669'}
            onMouseOut={(e) => e.target.style.background = '#10b981'}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('calendar')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <Phone size={20} />
            👉 Book a Call Now
          </Link>

          {/* Calendly Embed */}
          <div id="calendar" style={{ marginTop: '2rem' }}>
            <CalendlyEmbed 
              url="https://calendly.com/brightsonaccounting"
              height="700px"
            />
          </div>
        </div>

        {/* What Happens Next */}
        <div style={{ textAlign: 'left', marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#1e3a8a' }}>
            What happens next?
          </h3>
          <ul style={{ color: '#4b5563', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
            <li>We'll review your details and estimated savings</li>
            <li>One of our FCCA-qualified accountants will contact you within 24 hours</li>
            <li>We'll discuss how we can help reduce your tax bill legally</li>
            <li>You'll receive a tailored plan with exact savings</li>
          </ul>
        </div>

        {/* Additional Links */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '2rem' }}>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
            In the meantime, you might find these helpful:
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/blog" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>
              Read our blog
            </Link>
            <span style={{ color: '#d1d5db' }}>•</span>
            <Link to="/services" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>
              Our services
            </Link>
            <span style={{ color: '#d1d5db' }}>•</span>
            <Link to="/corporation-tax-calculator" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>
              Try calculator again
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
